import type { Id, RestoreArchiveMode, ScriptDocument } from "@supermanus/document-model";
import {
  VAULT_RETENTION_DAYS,
  formatArchiveOccurrenceLabel,
  formatEpisodeDisplayTitle,
  getEpisodeScenes,
  isNormalEpisode,
  listArchiveEpisodes,
  listArchivedVersions,
  listSubmittedEpisodes,
  listTrashEpisodes,
  vaultDaysRemaining,
  vaultExpiresAtIso,
  vaultRetentionStartedAt,
} from "@supermanus/document-model";
import { useMemo, useState } from "react";
import { RestoreArchiveDialog } from "./RestoreArchiveDialog";

function formatDateDa(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("da-DK", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function formatExpiryDateDa(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("da-DK", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function vaultExpiryText(
  scene: {
    trashedAt?: string;
    archivedAt?: string;
    updatedAt: string;
  },
  kind: "trash" | "archive",
): string {
  const started = vaultRetentionStartedAt(scene, kind);
  const days = vaultDaysRemaining(started);
  const expires = vaultExpiresAtIso(started);
  if (days <= 0) {
    return "Slettes automatisk snart";
  }
  if (days === 1) {
    return `Slettes automatisk i morgen (${formatExpiryDateDa(expires)})`;
  }
  return `Slettes automatisk om ${days} dage (${formatExpiryDateDa(expires)})`;
}

function confirmPurge(message: string): boolean {
  return window.confirm(`${message}\n\nDet kan ikke fortrydes.`);
}

export function ProductionVaultPanel({
  document,
  showEpisodeNumbers,
  readOnly,
  onOpenScene,
  onRestoreTrashEpisode,
  onPurgeTrashEpisode,
  onRestoreTrashScene,
  onPurgeTrashScene,
  onEmptyTrash,
  onRestoreArchiveScene,
  onPurgeArchiveScene,
  onPurgeArchiveEpisode,
  onEmptyArchive,
  onInsertSubmittedIntoProgram,
}: {
  document: ScriptDocument;
  showEpisodeNumbers: boolean;
  readOnly?: boolean;
  onOpenScene?: (sceneId: Id) => void;
  onRestoreTrashEpisode?: (episodeId: Id) => void;
  onPurgeTrashEpisode?: (episodeId: Id) => void;
  onRestoreTrashScene?: (sceneId: Id) => void;
  onPurgeTrashScene?: (sceneId: Id) => void;
  onEmptyTrash?: () => void;
  onRestoreArchiveScene?: (
    archivedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
  onPurgeArchiveScene?: (archivedSceneId: Id) => void;
  onPurgeArchiveEpisode?: (episodeId: Id) => void;
  onEmptyArchive?: () => void;
  onInsertSubmittedIntoProgram?: (
    submittedSceneId: Id,
    options: { mode: RestoreArchiveMode },
  ) => void;
}) {
  const trash = useMemo(() => listTrashEpisodes(document), [document]);
  const archive = useMemo(() => listArchiveEpisodes(document), [document]);
  const submitted = useMemo(() => listSubmittedEpisodes(document), [document]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(archive.length > 0);
  const [submittedOpen, setSubmittedOpen] = useState(submitted.length > 0);
  const [restoreArchiveId, setRestoreArchiveId] = useState<Id | null>(null);
  const [insertSubmittedId, setInsertSubmittedId] = useState<Id | null>(null);

  const trashCount = trash.reduce((n, ep) => n + ep.sceneIds.length, 0);
  const archiveCount = archive.reduce((n, ep) => n + ep.sceneIds.length, 0);
  const submittedCount = submitted.reduce((n, ep) => n + ep.sceneIds.length, 0);
  const restoreScene = restoreArchiveId
    ? document.scenes[restoreArchiveId]
    : null;
  const insertScene = insertSubmittedId
    ? document.scenes[insertSubmittedId]
    : null;

  return (
    <section className="sm-dash-card sm-vault-panel">
      <h2>Papirkurv, arkiv og afleveringer</h2>
      <p className="sm-vault-retention" role="note">
        Scener i papirkurv og arkiv slettes automatisk efter{" "}
        {VAULT_RETENTION_DAYS} dage. Afleverede scener bevares, indtil de
        indsættes i programmanus eller slettes manuelt.
      </p>

      <div className="sm-vault-block">
        <div className="sm-vault-block-head">
          <button
            type="button"
            className="sm-vault-toggle"
            aria-expanded={trashOpen}
            onClick={() => setTrashOpen((v) => !v)}
          >
            <span>Papirkurv</span>
            <span className="sm-vault-count">{trashCount}</span>
          </button>
          {!readOnly && onEmptyTrash && trashCount > 0 ? (
            <button
              type="button"
              className="sm-vault-danger sm-vault-clear"
              onClick={() => {
                if (
                  confirmPurge(
                    `Er du sikker på, at du vil rydde hele papirkurven (${trashCount} ${
                      trashCount === 1 ? "scene" : "scener"
                    })?`,
                  )
                ) {
                  onEmptyTrash();
                }
              }}
            >
              Ryd papirkurv
            </button>
          ) : null}
        </div>
        {trashOpen ? (
          trash.length === 0 ? (
            <p className="sm-vault-empty">Papirkurven er tom.</p>
          ) : (
            <ul className="sm-vault-list">
              {trash.map((ep) => {
                const label =
                  ep.originEpisodeTitle?.trim() ||
                  formatEpisodeDisplayTitle(ep, showEpisodeNumbers);
                const scenes = getEpisodeScenes(document, ep.id);
                const originLive =
                  ep.originEpisodeId &&
                  document.episodes[ep.originEpisodeId] &&
                  isNormalEpisode(
                    document,
                    document.episodes[ep.originEpisodeId]!,
                  )
                    ? document.episodes[ep.originEpisodeId]!
                    : null;
                return (
                  <li key={ep.id} className="sm-vault-episode">
                    <div className="sm-vault-episode-head">
                      <strong>{label}</strong>
                      <div className="sm-vault-actions">
                        {!readOnly && onRestoreTrashEpisode ? (
                          <button
                            type="button"
                            onClick={() => onRestoreTrashEpisode(ep.id)}
                          >
                            {originLive
                              ? "Gendan alle scener"
                              : "Gendan episode"}
                          </button>
                        ) : null}
                        {!readOnly && onPurgeTrashEpisode ? (
                          <button
                            type="button"
                            className="sm-vault-danger"
                            onClick={() => {
                              if (
                                confirmPurge(
                                  `Er du sikker på, at du vil slette «${label}» permanent fra papirkurven${
                                    scenes.length > 1
                                      ? ` (${scenes.length} scener)`
                                      : ""
                                  }?`,
                                )
                              ) {
                                onPurgeTrashEpisode(ep.id);
                              }
                            }}
                          >
                            Slet episode
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <ul className="sm-vault-scenes">
                      {scenes.map((scene) => (
                        <li key={scene.id}>
                          <div className="sm-vault-scene-meta">
                            {onOpenScene ? (
                              <button
                                type="button"
                                className="sm-vault-open"
                                onClick={() => onOpenScene(scene.id)}
                              >
                                Scene {scene.number}
                                {scene.title.trim()
                                  ? `: ${scene.title.trim()}`
                                  : ""}
                              </button>
                            ) : (
                              <span>
                                Scene {scene.number}
                                {scene.title.trim()
                                  ? `: ${scene.title.trim()}`
                                  : ""}
                              </span>
                            )}
                            <span className="sm-vault-expiry">
                              {vaultExpiryText(scene, "trash")}
                            </span>
                          </div>
                          <div className="sm-vault-actions">
                            {onOpenScene ? (
                              <button
                                type="button"
                                onClick={() => onOpenScene(scene.id)}
                              >
                                Åbn
                              </button>
                            ) : null}
                            {!readOnly &&
                            onRestoreTrashScene &&
                            originLive ? (
                              <button
                                type="button"
                                onClick={() => onRestoreTrashScene(scene.id)}
                              >
                                Gendan
                              </button>
                            ) : null}
                            {!readOnly && onPurgeTrashScene ? (
                              <button
                                type="button"
                                className="sm-vault-danger"
                                onClick={() => {
                                  if (
                                    confirmPurge(
                                      "Er du sikker på, at du vil slette scenen permanent fra papirkurven?",
                                    )
                                  ) {
                                    onPurgeTrashScene(scene.id);
                                  }
                                }}
                              >
                                Slet
                              </button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>

      <div className="sm-vault-block">
        <div className="sm-vault-block-head">
          <button
            type="button"
            className="sm-vault-toggle"
            aria-expanded={archiveOpen}
            onClick={() => setArchiveOpen((v) => !v)}
          >
            <span>Arkiv</span>
            <span className="sm-vault-count">{archiveCount}</span>
          </button>
          {!readOnly && onEmptyArchive && archiveCount > 0 ? (
            <button
              type="button"
              className="sm-vault-danger sm-vault-clear"
              onClick={() => {
                if (
                  confirmPurge(
                    `Er du sikker på, at du vil rydde hele arkivet (${archiveCount} ${
                      archiveCount === 1 ? "version" : "versioner"
                    })?`,
                  )
                ) {
                  onEmptyArchive();
                }
              }}
            >
              Ryd arkiv
            </button>
          ) : null}
        </div>
        {archiveOpen ? (
          archive.length === 0 ? (
            <p className="sm-vault-empty">Arkivet er tomt.</p>
          ) : (
            <ul className="sm-vault-list">
              {archive.map((ep) => {
                const label =
                  ep.originEpisodeTitle?.trim() ||
                  formatEpisodeDisplayTitle(ep, showEpisodeNumbers);
                const scenes = getEpisodeScenes(document, ep.id);
                return (
                  <li key={ep.id} className="sm-vault-episode">
                    <div className="sm-vault-episode-head">
                      <strong>{label}</strong>
                      <div className="sm-vault-actions">
                        {!readOnly && onPurgeArchiveEpisode ? (
                          <button
                            type="button"
                            className="sm-vault-danger"
                            onClick={() => {
                              if (
                                confirmPurge(
                                  `Er du sikker på, at du vil slette alle arkiverede versioner for «${label}» permanent (${scenes.length} ${
                                    scenes.length === 1
                                      ? "version"
                                      : "versioner"
                                  })?`,
                                )
                              ) {
                                onPurgeArchiveEpisode(ep.id);
                              }
                            }}
                          >
                            Slet alle versioner
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <ul className="sm-vault-scenes">
                      {scenes.map((scene) => {
                        const liveId = scene.archiveOfSceneId;
                        const versions = liveId
                          ? listArchivedVersions(document, liveId)
                          : [];
                        const versionIndex = versions.findIndex(
                          (s) => s.id === scene.id,
                        );
                        const occurrence =
                          versionIndex >= 0
                            ? formatArchiveOccurrenceLabel(
                                scene,
                                versionIndex + 1,
                              )
                            : scene.title.trim() || "Unavngiven scene";
                        const realTitle =
                          scene.title.trim() || "Unavngiven scene";
                        const custom = scene.archiveLabel?.trim();
                        const sceneLabel = custom
                          ? `«${custom}» — ${realTitle}`
                          : `${realTitle}${
                              versionIndex >= 0
                                ? ` (v${versionIndex + 1})`
                                : ""
                            }`;
                        const openLabel = custom
                          ? `${sceneLabel}${
                              scene.archivedAt
                                ? ` · arkiveret ${formatDateDa(scene.archivedAt)}`
                                : ""
                            }`
                          : `${sceneLabel}${
                              scene.archivedAt
                                ? ` — arkiveret ${formatDateDa(scene.archivedAt)}`
                                : ""
                            }`;
                        return (
                          <li key={scene.id}>
                            <div className="sm-vault-scene-meta">
                              {onOpenScene ? (
                                <button
                                  type="button"
                                  className="sm-vault-open"
                                  onClick={() => onOpenScene(scene.id)}
                                  title={occurrence}
                                >
                                  {openLabel}
                                </button>
                              ) : (
                                <span title={occurrence}>{openLabel}</span>
                              )}
                              <span className="sm-vault-expiry">
                                {vaultExpiryText(scene, "archive")}
                              </span>
                            </div>
                            <div className="sm-vault-actions">
                              {onOpenScene ? (
                                <button
                                  type="button"
                                  onClick={() => onOpenScene(scene.id)}
                                >
                                  Åbn
                                </button>
                              ) : null}
                              {!readOnly && onRestoreArchiveScene ? (
                                <button
                                  type="button"
                                  onClick={() => setRestoreArchiveId(scene.id)}
                                >
                                  Gendan til manus
                                </button>
                              ) : null}
                              {!readOnly && onPurgeArchiveScene ? (
                                <button
                                  type="button"
                                  className="sm-vault-danger"
                                  onClick={() => {
                                    if (
                                      confirmPurge(
                                        `Er du sikker på, at du vil slette den arkiverede version «${sceneLabel}» permanent?`,
                                      )
                                    ) {
                                      onPurgeArchiveScene(scene.id);
                                    }
                                  }}
                                >
                                  Slet version
                                </button>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>

      <div className="sm-vault-block">
        <div className="sm-vault-block-head">
          <button
            type="button"
            className="sm-vault-toggle"
            aria-expanded={submittedOpen}
            onClick={() => setSubmittedOpen((v) => !v)}
          >
            <span>Afleverede scener</span>
            <span className="sm-vault-count">{submittedCount}</span>
          </button>
        </div>
        {submittedOpen ? (
          submitted.length === 0 ? (
            <p className="sm-vault-empty">Ingen afleverede scener endnu.</p>
          ) : (
            <ul className="sm-vault-list">
              {submitted.map((ep) => {
                const label =
                  ep.originEpisodeTitle?.trim() ||
                  formatEpisodeDisplayTitle(ep, showEpisodeNumbers);
                const scenes = getEpisodeScenes(document, ep.id);
                return (
                  <li key={ep.id} className="sm-vault-episode">
                    <div className="sm-vault-episode-head">
                      <strong>{label}</strong>
                    </div>
                    <ul className="sm-vault-scenes">
                      {scenes.map((scene) => {
                        const name =
                          scene.title.trim() || "Unavngiven scene";
                        const by = scene.submittedByName?.trim();
                        const when = scene.submittedAt
                          ? formatDateDa(scene.submittedAt)
                          : "";
                        const openLabel = [
                          `Scene ${scene.number}: ${name}`,
                          by ? `Afleveret af ${by}` : null,
                          when ? when : null,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <li key={scene.id}>
                            <div className="sm-vault-scene-meta">
                              {onOpenScene ? (
                                <button
                                  type="button"
                                  className="sm-vault-open"
                                  onClick={() => onOpenScene(scene.id)}
                                >
                                  {openLabel}
                                </button>
                              ) : (
                                <span>{openLabel}</span>
                              )}
                            </div>
                            <div className="sm-vault-actions">
                              {onOpenScene ? (
                                <button
                                  type="button"
                                  onClick={() => onOpenScene(scene.id)}
                                >
                                  Åbn
                                </button>
                              ) : null}
                              {!readOnly && onInsertSubmittedIntoProgram ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setInsertSubmittedId(scene.id)
                                  }
                                >
                                  Indsæt i programmanus
                                </button>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>

      {restoreArchiveId && restoreScene && onRestoreArchiveScene ? (
        <RestoreArchiveDialog
          sceneLabel={(() => {
            const name = restoreScene.title.trim() || "Unavngiven scene";
            const liveId = restoreScene.archiveOfSceneId;
            const versions = liveId
              ? listArchivedVersions(document, liveId)
              : [];
            const versionIndex = versions.findIndex(
              (s) => s.id === restoreArchiveId,
            );
            return versionIndex >= 0
              ? `«${name}» (v${versionIndex + 1})`
              : `«${name}»`;
          })()}
          onCancel={() => setRestoreArchiveId(null)}
          onConfirm={(mode) => {
            const id = restoreArchiveId;
            setRestoreArchiveId(null);
            onRestoreArchiveScene(id, { mode });
          }}
        />
      ) : null}

      {insertSubmittedId && insertScene && onInsertSubmittedIntoProgram ? (
        <RestoreArchiveDialog
          title="Indsæt i programmanus"
          lead={`Du er ved at indsætte scene ${insertScene.number} «${
            insertScene.title.trim() || "Unavngiven scene"
          }» i programmanus. Vælg, hvad der skal ske med den nuværende scene.`}
          legend="Hvordan vil du indsætte?"
          confirmLabel="Indsæt"
          replaceHint={`den nuværende version lægges i papirkurven (slettes automatisk efter ${VAULT_RETENTION_DAYS} dage)`}
          keepHint="den afleverede version indsættes som en ekstra scene"
          sceneLabel={`scene ${insertScene.number}`}
          onCancel={() => setInsertSubmittedId(null)}
          onConfirm={(mode) => {
            const id = insertSubmittedId;
            setInsertSubmittedId(null);
            onInsertSubmittedIntoProgram(id, { mode });
          }}
        />
      ) : null}
    </section>
  );
}
